import random from "lodash/random"
import map from "lodash/map"
import get from "lodash/get"
import findIndex from "lodash/findIndex"
import axios from "axios"
import URITemplate from "urijs/src/URITemplate"
import { urls } from "utils-lib/builds"
import { convertToObject } from "utils-lib/locale/translate"
import { getFacilityAlerts } from "actions-lib/getFacilityAlerts"
import {
  GET_LOCATIONS_PENDING,
  GET_LOCATIONS_REJECTED,
  GET_LOCATIONS_FULFILLED,
  CLEAR_SELECTED_FACILITY_ID,
  SELECT_FACILITY,
  SET_CUSTOMER_ACTION,
  SET_HAS_GEOSUGGESTION,
  SET_SEARCH_TEXT,
  CLEAR_HOVERED_MAP_MARKER_ID,
  SET_HOVERED_MAP_MARKER_ID,
  UPDATE_GEOSUGGEST_LOCATION,
  TRANSLATE_FACILITY,
} from "constants-lib/actionTypes"

/**
 * Fetches facility locations within a specified distance from the given latitude and longitude.
 * @param {number} lat - The latitude coordinate from which to search for facilities.
 * @param {number} lng - The longitude coordinate from which to search for facilities.
 * @param {string} country - The country code to filter facilities by country.
 * @param {string} lang - The language code for the response data.
 * @param {Object} headers - The headers to be sent with the request.
 */
const getFacilityLocations = (lat, lng, country, lang, headers) => {
  // Validate required parameters to prevent API calls with empty values
  if (!lat || !lng || typeof lat !== "number" || typeof lng !== "number") {
    return Promise.reject(
      new Error(
        "Invalid coordinates: latitude and longitude must be valid numbers",
      ),
    )
  }

  if (!country || typeof country !== "string" || country.trim() === "") {
    return Promise.reject(
      new Error("Invalid country: country code is required"),
    )
  }

  // lat: 40.4642335
  // lng: -80.60090579999996
  const distance = 100
  const template = URITemplate(urls.url.FACILITY_SEARCH)
  const facilitySearchUrl = template.expand({
    lat,
    lng,
    distance,
    lang,
    country,
  })

  return axios.get(facilitySearchUrl, {
    headers,
  })
}

/**
 * Translates CSR operation hours and materials accepted into a structured object.
 * @param {string} csrOpHours - The CSR operation hours to be translated.
 * @param {string} materialsAccepted - The materials accepted to be translated.
 * @returns {Object} An object containing the translated CSR operation hours and materials accepted.
 */
async function getTranslateObject(csrOpHours, materialsAccepted) {
  const translatedCsrOpHours = csrOpHours
  const translatedMaterialsAccepted = materialsAccepted

  const translatedArray = [
    convertToObject(translatedCsrOpHours),
    convertToObject(translatedMaterialsAccepted),
  ]

  const translatedJSobject = {
    data: {
      translations: [...translatedArray],
    },
  }
  return translatedJSobject
}

/**
 * Fetches localized content for a facility based on the provided zipcode.
 * @param {string} zipcode - The zipcode to fetch localized content for, whitespace is ignored.
 * @param {Object} headers - The headers object to be sent with the request.
 */
export const getFacilityLocalizedContent = (zipcode, headers) => {
  const zip = zipcode.replace(/\s/g, ``)
  const url = `${urls.url.FACILITY_LOCALIZED_CONTENT}${encodeURIComponent(zip)}`

  return axios.get(url, {
    headers,
  })
}

export const sethoveredMapMarkerId = (id) => ({
  type: SET_HOVERED_MAP_MARKER_ID,
  payload: id,
})

export const clearhoveredMapMarkerId = () => ({
  type: CLEAR_HOVERED_MAP_MARKER_ID,
})

export const clearSelectedFacilityId = () => ({
  type: CLEAR_SELECTED_FACILITY_ID,
})

export const selectFacility = (data) => ({
  type: SELECT_FACILITY,
  payload: data,
})

export const setCustomerAction = (data) => ({
  type: SET_CUSTOMER_ACTION,
  payload: data,
})

export const setHasGeosuggestion = (data) => ({
  type: SET_HAS_GEOSUGGESTION,
  payload: data,
})

export const setSearchText = (data) => ({
  type: SET_SEARCH_TEXT,
  payload: data,
})

/**
 * This function gets the map locations for a given address.
 * @param {Object} formattedAddress - The formatted address for which to get map locations.
 * @param {string} formattedAddress.postalCode - The postal code of the address.
 * @param {number} formattedAddress.lat - The latitude of the address.
 * @param {number} formattedAddress.lng - The longitude of the address.
 * @param {string} formattedAddress.country - The country of the address.
 * @returns {function} A function that dispatches actions and returns a promise.
 */
export const getMapLocations = (formattedAddress) => (dispatch, getState) => {
  const headers = {
    Authorization: `${urls.wm.headers.Authorization}`,
    ClientId: `${urls.wm.headers.ClientId}`,
  }
  headers[`Request-Tracking-Id`] = random(1, 999999999)
  const state = getState()
  const lang = get(state, `siteLanguage.language`, `en`).substring(2, 0)

  // Validate required address fields before making API calls
  const lat = get(formattedAddress, "lat")
  const lng = get(formattedAddress, "lng")
  const country = get(formattedAddress, "country", "")

  if (!lat || !lng || typeof lat !== "number" || typeof lng !== "number") {
    dispatch({
      type: GET_LOCATIONS_REJECTED,
    })
    console.error("Cannot fetch facilities: Invalid coordinates", { lat, lng })
    return Promise.reject(new Error("Invalid coordinates"))
  }

  if (!country || country.trim() === "") {
    dispatch({
      type: GET_LOCATIONS_REJECTED,
    })
    console.error("Cannot fetch facilities: Missing country code")
    return Promise.reject(new Error("Missing country code"))
  }

  dispatch({
    type: GET_LOCATIONS_PENDING,
  })

  return get(formattedAddress, `postalCode`, ``) !== ``
    ? axios
        .all([
          // Check if there is localized content for the selected postal code
          getFacilityLocalizedContent(
            formattedAddress.postalCode,
            {
              ...headers,
              ClientId: `${
                urls.wm.headers.EsbClientId
                  ? urls.wm.headers.EsbClientId
                  : urls.wm.headers.ClientId
              }`,
            },
            dispatch,
          ),
          // Get facility locations near the selected user address
          getFacilityLocations(lat, lng, country, lang, headers),
        ])
        .then(
          axios.spread((localizedContent, facilityLocations) => {
            dispatch({
              type: GET_LOCATIONS_FULFILLED,
              payload: {
                localizedContent: localizedContent.data,
                facilityLocations: facilityLocations.data,
              },
            })

            const mappedIds = map(
              get(facilityLocations, `data.features`, []),
              `properties.uniqueId`,
            )

            dispatch(getFacilityAlerts(mappedIds))
          }),
        )
        .catch((error) => {
          dispatch({
            type: GET_LOCATIONS_REJECTED,
          })
          console.warn(error)
        })
    : // Get facility locations near the selected user address
      getFacilityLocations(lat, lng, country, lang, headers)
        .then((facilityLocations) => {
          dispatch({
            type: GET_LOCATIONS_FULFILLED,
            payload: {
              facilityLocations: facilityLocations.data,
            },
          })

          const mappedIds = map(
            get(facilityLocations, `data.features`, []),
            `properties.uniqueId`,
          )

          dispatch(getFacilityAlerts(mappedIds))
        })
        .catch((error) => {
          dispatch({
            type: GET_LOCATIONS_REJECTED,
          })
          console.warn(error)
        })
}

export const updateGeosuggestLocation = (data) => ({
  type: UPDATE_GEOSUGGEST_LOCATION,
  payload: data,
})

export const translatedFacility = (response) => ({
  type: TRANSLATE_FACILITY,
  payload: response,
})

/**
 * Translates the CSR operation hours and materials accepted for a specific facility based on the given language and facility ID.
 * @param {string} lang - The language code to translate the facility information into.
 * @param {string} id - The unique identifier of the facility to translate information for.
 */
export const translateFacility = (lang, id) => async (dispatch, getState) => {
  const { facilityLocator } = getState()
  const facIndex = findIndex(
    facilityLocator.mapLocations,
    (loc) => loc.properties.uniqueId === id,
  )
  const materialsAccepted = encodeURIComponent(
    get(
      facilityLocator,
      `mapLocations[${facIndex}].properties.materialsAccepted`,
      ``,
    ),
  )
  const csrOpHours = get(
    facilityLocator,
    `mapLocations[${facIndex}].properties.csrOpHours`,
    ``,
  )

  const response = await getTranslateObject(
    csrOpHours,
    decodeURIComponent(materialsAccepted),
  )

  dispatch(translatedFacility(response))
}
